// ManuallyVerifiedBanner.tsx
//
// Reusable single-line trust banner. Honest statement that every pharmacy
// on Script Unlock is manually verified by the team before they can bid.
//
// Drop into key conversion pages: homepage, /pet-medication hub,
// /pharmacy-verification, /for-veterinarians.
//
// SSR-safe: no browser globals, no module-scope side effects.

import Link from 'next/link';
import { ShieldCheck } from 'lucide-react';

interface Props {
  /** Optional context line. Defaults to general statement. */
  context?: 'pharmacies' | 'vets' | 'pets' | 'all';
  /** Variant: 'inline' (slim row), 'card' (boxed card) */
  variant?: 'inline' | 'card';
  className?: string;
}

const CONTEXT_LINES: Record<NonNullable<Props['context']>, string> = {
  pharmacies:
    'Every pharmacy on Script Unlock is manually verified by our team before it can quote on your prescription.',
  vets:
    'Every vet-that-compounds on Script Unlock is manually verified by our team before they can quote on prescriptions.',
  pets:
    'All pharmacies and compounding vets on Script Unlock are manually verified by our team before they can quote on your pet’s prescription.',
  all:
    'Every pharmacy and vet-that-compounds on Script Unlock is manually verified by our team before they can quote.',
};

export default function ManuallyVerifiedBanner({
  context = 'pharmacies',
  variant = 'inline',
  className = '',
}: Props) {
  const text = CONTEXT_LINES[context];

  if (variant === 'card') {
    return (
      <div
        className={`rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-5 ${className}`}
      >
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-secondary flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-foreground mb-1">Manually verified</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {text}{' '}
              <Link
                href="/pharmacy-verification"
                className="text-secondary hover:underline whitespace-nowrap"
              >
                See our pharmacy verification process
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Inline variant (slim banner)
  return (
    <div
      className={`inline-flex flex-wrap items-center gap-2 text-xs md:text-sm text-muted-foreground ${className}`}
    >
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-700 font-medium">
        <ShieldCheck className="w-3.5 h-3.5" />
        Manually verified
      </span>
      <span className="text-muted-foreground">{text}</span>
      <Link
        href="/pharmacy-verification"
        className="text-secondary hover:underline whitespace-nowrap"
      >
        See our pharmacy verification process
      </Link>
    </div>
  );
}
